import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Backdrop, CircularProgress } from '@material-ui/core';
import { trackEvent } from '../../utils/eventTracking';
import { UserProfileContext } from '../../UserProfileContext';
import { DataSpaceViewContext } from '../my_dataspaces/context/DataSpaceViewContext';
import MyDataSpacesViewContext from '../my_dataspaces/context/MyDataSpacesViewContext';
import { cloneDataSpace, getUserProfile } from '../../api_helper/api';
import { getUserMessage } from '../../utils/handleError';
import PageContext from '../../context/PageContext';
import SnackbarAlertContext from '../../context/SnackbarAlertContext';
import AuthModal from '../ui_elements/AuthModal';
import SelectFantasyLeagueModal from '../ui_elements/SelectFantasyLeagueModal';

const AddToMyAccountButton = () => {
	let history = useHistory();
	const pageContext = useContext(PageContext);
	const [userProfile, setUserProfile] = useContext(UserProfileContext);
	const dataSpaceViewContext = useContext(DataSpaceViewContext);
	const myDataSpacesViewContext = useContext(MyDataSpacesViewContext);
	const snackbarAlertContext = useContext(SnackbarAlertContext);

	const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
	const [isSelectFantasyLeagueModalOpen, setIsSelectFantasyLeagueModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	let author = dataSpaceViewContext.dataSpace.originalAuthor || dataSpaceViewContext.dataSpace.createdBy;

	const handleCloneDataSpace = async (fantasyLeague) => {
		try {
			setIsLoading(true);

			const res = await cloneDataSpace({
				dataSpaceId: dataSpaceViewContext.dataSpace._id,
				userId: userProfile._id,
				fantasyLeagueId: fantasyLeague?._id,
			});

			pageContext.setPage('mydataspaces');
			myDataSpacesViewContext.setMyDataSpacesView((prevState) => ({
				...prevState,
				selectedDataSpace: { name: res.data.name, id: res.data.dataSpaceId },
			}));
			myDataSpacesViewContext.setSelectedDataSpace({ name: res.data.name, id: res.data.dataSpaceId });
			myDataSpacesViewContext.changeViewHandler('dataSpaceViewNew');
			snackbarAlertContext.setSnackbarAlert({
				msg: 'DataSpace successfully added to your account',
				severity: 'success',
			});
			history.push('/app');

			trackEvent({
				userDetails: { userId: userProfile?._id, email: userProfile?.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User added shared DataSpace to their account.',
				},
			});
		} catch (error) {
			if (error.response?.data?.code === 'CLONE_DATASPACE_NOT_PUBLIC') {
				pageContext.setPage('CommunityDataSpaces');
				snackbarAlertContext.setSnackbarAlert({
					msg: 'This DataSpace does not have the permissions to be added to your account. Please see some of our Community DataSpaces below.',
					severity: 'error',
					autoHide: 8000,
				});
				history.push('/app');

				trackEvent({
					userDetails: { userId: userProfile?._id, email: userProfile?.email },
					eventDetails: {
						types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
						eventName:
							'WebApp_User attempted to add non-Community DataSpace to their account and was redirected to Community DataSpaces page.',
					},
				});
				return;
			}
			snackbarAlertContext.setSnackbarAlert({ msg: getUserMessage(error), severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	const handleAuthSuccess = async () => {
		const fantasyLeagueDataset = dataSpaceViewContext.dataSpace.datasets.find((dataset) =>
			['Yahoo! Fantasy Sports', 'Fantrax'].includes(dataset.tags.api),
		);

		if (fantasyLeagueDataset) {
			setIsSelectFantasyLeagueModalOpen(true);
			return;
		}

		await handleCloneDataSpace();
	};

	const handleSelectFantasyLeagueSuccess = async (fantasyLeague) => {
		setIsSelectFantasyLeagueModalOpen(false);
		await handleCloneDataSpace(fantasyLeague);
	};

	const handleAuth = () => {
		if (!localStorage.getItem('jwt')) {
			setIsAuthModalOpen(true);
			return;
		} else {
			trackEvent({
				userDetails: { userId: userProfile?._id, email: userProfile?.email },
				eventDetails: {
					types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
					eventName: 'WebApp_User already authenticated.',
				},
			});
		}

		handleAuthSuccess(userProfile);
	};

	return (
		<>
			<Backdrop open={isLoading} sx={{ width: '100vw', height: '100vh', zIndex: '10000', color: '#FFFFFF' }}>
				<CircularProgress color="inherit" />
			</Backdrop>

			<AuthModal
				isOpen={isAuthModalOpen}
				onClose={() => setIsAuthModalOpen(false)}
				onSuccess={async (result) => {
					localStorage.setItem('jwt', result.token);
					const user = await getUserProfile();
					setUserProfile(user);
					setIsAuthModalOpen(false);
					handleAuthSuccess(user);
				}}
			/>

			<SelectFantasyLeagueModal
				isOpen={isSelectFantasyLeagueModalOpen}
				onClose={() => setIsSelectFantasyLeagueModalOpen(false)}
				onSuccess={handleSelectFantasyLeagueSuccess}
			/>

			<Button
				color={
					window.kmval?.addDataSpaceCta === 'addToMyAccount'
						? 'success'
						: window.kmval?.addDataSpaceCta === 'saveToMyDashboard'
							? 'secondary'
							: 'primary'
				}
				variant="contained"
				style={{ color: 'white' }}
				onClick={() => {
					trackEvent({
						userDetails: { userId: userProfile?._id, email: userProfile?.email },
						eventDetails: {
							types: ['KissMetrics', 'Segment', 'AppInsights', 'GA4'],
							eventName: 'WebApp_User clicked on addDataSpaceCta button from shared DataSpace.',
						},
					});

					handleAuth();
				}}
			>
				{window.kmval?.addDataSpaceCta === 'addToMyAccount'
					? 'Add to My Account'
					: window.kmval?.addDataSpaceCta === 'saveToMyDashboard'
						? 'Save to My Dashboard'
						: 'Sign Up'}
			</Button>
		</>
	);
};

export default AddToMyAccountButton;
